import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';

/**
 * Handle 401 errors globally as a safety net for expired tokens.
 *
 * Delegates to performAutoLogout() (also triggered directly in api-utils.ts),
 * which clears auth synchronously, runs the full cleanup, and hard-redirects
 * to login exactly once -- re-entrant calls are no-ops, so the old
 * refetch -> 401 -> cache-clear loop cannot restart itself.
 *
 * Errors flagged skipAuthLogout come from calls that opted out of auto-logout
 * (e.g. the background subscription check) and must not log the user out.
 * Dynamic import to avoid a static cycle (cleanup.ts imports queryClient).
 */
function handleGlobalError(error: unknown) {
  const err = error as { status?: number; code?: number; skipAuthLogout?: boolean };
  if (err?.skipAuthLogout) return;
  if (err?.status === 401 || err?.code === 401) {
    void import('@/lib/cleanup').then(({ performAutoLogout }) => performAutoLogout());
  }
}

/**
 * React Query Client Configuration
 * 
 * Important Notes:
 * - gcTime (garbage collection time) is set to 24 hours to work with persistence
 * - gcTime must be >= persistOptions.maxAge to prevent premature cache cleanup
 * - staleTime determines when queries refetch (5 minutes)
 * - gcTime determines when unused data is removed from cache (24 hours)
 * - Global error handlers redirect to login on 401 (expired token)
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleGlobalError,
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - data is fresh for 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache for 24 hours (matches persistence maxAge)
      retry: (failureCount, error) => {
        // Don't retry on 401 errors (unauthorized)
        const err = error as { status?: number; code?: number };
        if (err?.status === 401 || err?.code === 401) {
          return false;
        }
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && error.message.includes('4')) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
});

